import React, { useEffect, useState } from "react";
import {
  TableContainer,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Box,
  Toolbar,
  Typography,
  Divider,
  TableSortLabel,
  TablePagination,
} from "@mui/material";
import { MyDatePicker } from "../../components/my-date-picker/MyDatePicker";
import { MyButton } from "../../components/my-button/MyButton";
import ClaimApiService from "../../adapters/xhr/ClaimApiService";
import { useTranslation } from "react-i18next";
import { MyDropdown } from "../../components/my-dropdown/MyDropdown";
import SnackbarUtils from "../../SnackbarUtils";
import {
  lngs,
  ReceiptType,
  Reports,
} from "../../Constant";
import { formatDate, formatTime } from "../../Utils";

export const ReportsPage = () => {
  const [totalReimbursementSEK, setTotalReimbursementSEK] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [rows, setRows] = useState([]);
  const [startDate, setStartDate] = useState();
  const [startDateErr, setStartDateErr] = useState(false);
  const [endDate, setEndDate] = useState();
  const [endDateErr, setEndDateErr] = useState();
  const { t, i18n } = useTranslation();
  const options = [
    { key: Reports.Fraud, value: t("fraud") },
    { key: Reports.Accounting, value: t("accounting") },
  ];
  const [fileExport, setFileExport] = useState({
    key: Reports.Fraud,
    value: t("fraud"),
  });
  const [sortBy, setSortBy] = useState("ClaimWithoutTicketNumber");
  const [ascending, setAscending] = useState(false);
  const [showMore, setShowMore] = useState([]);
  const claimColumns = [
    {
      id: "CaseNumber",
      label: t("case_number"),
      minWidth: 170,
      sortable: true,
    },
    { id: "ClaimStatus", label: t("status"), minWidth: 170, sortable: true },
    {
      id: "Created",
      label: t("registred_datetime"),
      minWidth: 170,
      sortable: true,
    },
    {
      id: "TransportationNumber",
      label: t("transportation_number"),
      minWidth: 170,
      sortable: true,
    },
    {
      id: "ReimbursementSEK",
      label: t("reimbursement_SEK"),
      minWidth: 170,
      sortable: true,
    },
    { id: "TicketPriceSEK", label: t("ticket_price_SEK"), minWidth: 170 },
    { id: "DelayMin", label: t("delay_min"), minWidth: 170 },
    {
      id: "From",
      label: t("from"),
      minWidth: 180,
      sortable: false,
    },
    {
      id: "To",
      label: t("to"),
      minWidth: 180,
      sortable: false,
    },
    { id: "Comment", label: t("comment"), minWidth: 170, sortable: true },
    { id: "TicketValidFrom", label: t("ticket_valid_from"), minWidth: 180 },
    { id: "TicketValidTo", label: t("ticket_valid_to"), minWidth: 180 },
    {
      id: "TicketNumber",
      label: t("ticket_number_ticket_card_number"),
      minWidth: 170,
      sortable: true,
    },
    {
      id: "SentToEasitGO",
      label: t("sent_to_easitgo"),
      minWidth: 170,
      sortable: true,
    },
  ];
  const fraudReportColumns = [
    {
      id: "IdentityNumber",
      label: t("personal_ID"),
      minWidth: 170,
      sortable: true,
    },
    { id: "FirstName", label: t("firstname"), minWidth: 170, sortable: true },
    { id: "SurName", label: t("surname"), minWidth: 170, sortable: true },
    {
      id: "ReimbursementSEK",
      label: t("reimbursement_SEK"),
      minWidth: 170,
      sortable: true,
    },
    {
      id: "TicketPriceSEK",
      label: t("ticket_price_SEK"),
      minWidth: 170,
      sortable: true,
    },
    {
      id: "TotalClaims",
      label: t("number_of_claims"),
      minWidth: 170,
      sortable: true,
    },
    {
      id: "ClaimWithoutTicketNumber",
      label: t("number_of_claims_without_ticket"),
      minWidth: 170,
      sortable: true,
    },
    {
      id: "CaseNumber",
      label: t("case_number"),
      minWidth: 170,
      sortable: true,
    },
  ];
  const [columns, setColumns] = useState(fraudReportColumns);

  const EnhancedTableToolbar = () => {
    let title = "";
    switch (fileExport.key) {
      case Reports.Accounting:
        title = `${t("accounting_report")} (Total summa: ${totalReimbursementSEK} ${t("kr")})`;
        break;
      case Reports.Fraud:
        title = t("fraud_report");
        break;
      default:
        break;
    }

    return (
      <Toolbar>
        <Typography
          sx={{ flex: "1 1 100%" }}
          variant="h6"
          id="tableTitle"
          component="div"
        >
          {title}
        </Typography>
      </Toolbar>
    );
  };

  useEffect(() => {
    switch (fileExport.key) {
      case Reports.Accounting:
        fetchAccountingReport();
        fetchTotalReimbursement();
        break;
      case Reports.Fraud:
        fetchFraudReport();
        break;
      default:
        break;
    }
  }, [fileExport, startDate, endDate, sortBy, ascending, rowsPerPage, page]);

  const fetchFraudReport = async () => {
    let payload = {
      ascending: ascending,
      sortBy: sortBy,
      pageNumber: page + 1,
      pageSize: rowsPerPage,
    };
    if (startDate) {
      payload.startDate = formatDate(startDate);
    }
    if (endDate) {
      payload.endDate = formatDate(endDate);
    }
    let response = await ClaimApiService.getFraudReport(payload);
    if (response?.isSuccess && response.data.items.length >= 0) {
      let data = response.data.items.map((option) => {
        let rObj = {};
        rObj["IdentityNumber"] = option.identityNumber;
        rObj["CaseNumber"] = option.caseNumber;
        rObj["FirstName"] = option.firstName;
        rObj["SurName"] = option.surName;
        rObj["ReimbursementSEK"] = option.reimbursementSEK;
        rObj["TicketPriceSEK"] = option.ticketPriceSEK;
        rObj["ClaimWithoutTicketNumber"] = option.claimWithoutTicketNumber;
        rObj["TotalClaims"] = option.totalClaims;
        rObj["FromDate"] = option.fromDate;
        rObj["ToDate"] = option.toDate;
        return rObj;
      });
      setRows(data);
      setTotalPages(response.data.totalCount);
      setColumns(fraudReportColumns);
    }
  };

  const fetchTotalReimbursement = async () => {
    let payload = {};

    if (startDate) payload.startDate = formatDate(startDate);
    if (endDate) payload.endDate = formatDate(endDate);

    let response = await ClaimApiService.getTotalReimbursementSEK(payload);
    if (response?.isSuccess) {
      setTotalReimbursementSEK(response.data);
    }
  };

  const fetchAccountingReport = async () => {
    let payload = {
      ascending: ascending,
      sortBy: sortBy,
      pageNumber: page + 1,
      pageSize: rowsPerPage,
    };
    if (startDate) {
      payload.startDate = formatDate(startDate);
    }
    if (endDate) {
      payload.endDate = formatDate(endDate);
    }
    let response = await ClaimApiService.getAccountingReport(payload);
    if (response?.isSuccess && response.data.items.length >= 0) {
      let data = response.data.items.map((option) => {
        let rObj = {};
        rObj["Id"] = option.id;
        rObj["ExternalTicketType"] = option.externalTicketType;
        rObj["CaseNumber"] = option.caseNumber;
        rObj["FromDate"] = option.fromDate;
        rObj["ToDate"] = option.toDate;
        rObj["ClaimStatus"] =
          i18n.language === lngs.en.code
            ? option.claimStatus?.name
            : option.claimStatus?.swedishName;
        rObj["Created"] =
          `${formatDate(option.created)} ${formatTime(option.created, true)}`;
        rObj["TransportationNumber"] =
          option.transportationLine ?? option.transportationNumber;
        rObj["ReimbursementSEK"] = option.reimbursementSEK;
        rObj["TicketReimbursementSEK"] =
          option.ticketReimbursementSEK > 0
            ? option.ticketReimbursementSEK
            : "";
        let foodCost = option.claimReceipts?.find(
          (item) => item.receiptType.name == ReceiptType.Food,
        )?.totalCost;
        rObj["FoodCost"] = foodCost && foodCost > 0 ? foodCost : "";
        let accommodationCost = option.claimReceipts?.find(
          (item) => item.receiptType.name == ReceiptType.Extra,
        )?.totalCost;
        rObj["AccommodationCost"] =
          accommodationCost && accommodationCost > 0 ? accommodationCost : "";
        let transportationCost = option.claimReceipts?.find(
          (item) => item.receiptType.name == ReceiptType.Transportation,
        )?.totalCost;
        rObj["TransportationCost"] =
          transportationCost && transportationCost > 0
            ? transportationCost
            : "";
        rObj["TicketPriceSEK"] = option.ticketPriceSEK;
        rObj["TicketPriceSEKIncludeVAT"] = option.ticketPriceSEK;
        rObj["DelayMin"] = option.delayMinutes;
        rObj["From"] = option.from;
        rObj["To"] = option.to;
        rObj["Comment"] = option.comment;
        rObj["DepartureDate"] =
          `${formatDate(option.departureDate)} ${formatTime(option.departureDate, true)}`;
        rObj["TicketValidFrom"] =
          `${formatDate(option.ticketValidFrom)} ${formatTime(option.ticketValidFrom, true)}`;
        rObj["TicketValidTo"] =
          `${formatDate(option.ticketValidTo)} ${formatTime(option.ticketValidTo, true)}`;
        rObj["TicketNumber"] = option.ticketNumber;
        rObj["SentToEasitGO"] =
          `${formatDate(option.sentToEasitGo)} ${formatTime(option.sentToEasitGo, true)}`;
        return rObj;
      });
      setRows(data);
      setTotalPages(response.data.totalCount);
      setTotalReimbursementSEK(
        response.data.items.reduce(
          (accumulator, object) => accumulator + object.reimbursementSEK,
          0,
        ),
      );
      setColumns(claimColumns);
    }
  };

  const handleSort = async (field, ascending) => {
    if (field === "Status")
      field =
        i18n.language === lngs.en.code ? "ClaimStatus" : "ClaimStatusSwedish";
    setSortBy(field);
    setAscending(ascending);
  };

  const handleFileExportChange = (fileExport) => {
    setFileExport(fileExport);
    setAscending(false);
    setSortBy("Created");
    setPage(0);
    setRowsPerPage(25);
    switch (fileExport.key) {
      case Reports.Accounting:
        break;
      case Reports.Fraud:
        setSortBy("ClaimWithoutTicketNumber");
        break;
      default:
        break;
    }
  };

  const exportAccountingFile = async () => {
    setStartDateErr(!startDate);
    setEndDateErr(!endDate);
    if (!startDate || !endDate || fileExport.key !== Reports.Accounting) return;
    let payload = {};
    if (startDate) {
      payload.fromDate = formatDate(startDate);
    }
    if (endDate) {
      payload.toDate = formatDate(endDate);
    }
    let result = await ClaimApiService.exportAccountingReport(payload);
    if (result?.isSuccess) {
      SnackbarUtils.success(t("export_ready"));
      fetchAccountingReport();
    } else SnackbarUtils.error(t("error"));
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  return (
    <React.Fragment>
      <Box sx={{ display: "flex" }} justifyContent="space-between">
        <Box sx={{ display: "flex" }}>
          <MyDropdown
            disableClearable={true}
            size="small"
            fullWidth={false}
            style={{ width: "15rem", margin: "1rem 0rem 1rem 1rem" }}
            onChange={(e, value) => handleFileExportChange(value)}
            label={t("report")}
            value={fileExport}
            options={options}
          />
          <MyDatePicker
            error={startDateErr}
            clearable={false}
            size="small"
            onAccept={(date) => setStartDate(date?.toString())}
            value={startDate}
            label={t("from")}
            style={{ width: "12rem", margin: "1rem 0 1rem 1rem" }}
          />
          <MyDatePicker
            error={endDateErr}
            clearable={false}
            size="small"
            onAccept={(date) => setEndDate(date?.toString())}
            value={endDate}
            label={t("to")}
            style={{ width: "12rem", margin: "1rem 0 1rem 1rem" }}
          />
          {fileExport.key !== Reports.Fraud && (
            <MyButton
              disabled={false}
              onClick={exportAccountingFile}
              text={t("send_for_payment")}
              value=""
              style={{ width: "auto", margin: "1rem 1rem 1rem 1rem" }}
            />
          )}
        </Box>
      </Box>
      <Divider />
      <EnhancedTableToolbar />
      <TableContainer>
        <Table stickyHeader aria-label="sticky table" size="small">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{ minWidth: column.minWidth }}
                >
                  {column.sortable ? (
                    <TableSortLabel
                      active={column.id === sortBy}
                      direction={ascending ? "asc" : "desc"}
                      onClick={(e) => handleSort(column.id, !ascending)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, key) => {
              return (
                <TableRow hover tabIndex={-1} key={key}>
                  {columns.map((column) => {
                    const value = row[column.id];
                    return (
                      <TableCell key={column.id} align={column.align}>
                        {column.format && typeof value === "number"
                          ? column.format(value)
                          : showMore.some((item) => item == `${key}`)
                            ? value
                            : value?.toString().substring(0, 40)}
                        {value?.toString().length > 40 &&
                          (showMore == [] ||
                            !showMore.some((item) => item == key)) && (
                            <i
                              className="selector"
                              onClick={() =>
                                setShowMore([...showMore, `${key}`])
                              }
                            >
                              {` ...`}
                            </i>
                          )}
                        {value?.toString().length > 40 &&
                          showMore != [] &&
                          showMore.some((item) => item == key) && (
                            <strong
                              color="primary.main"
                              className="selector"
                              onClick={() =>
                                setShowMore(
                                  [...showMore].filter((item) => item != key),
                                )
                              }
                            >
                              {` ${t("show_less")}`}
                            </strong>
                          )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[25, 50, 100]}
        component="div"
        count={totalPages}
        labelRowsPerPage={t("rows_per_page")}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </React.Fragment>
  );
};
